import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Mail, Play, RefreshCw, Send, CheckCircle, AlertTriangle, Activity, Calendar, Clock,
} from "lucide-react";

const SECTION_LABELS = {
  estado_general: "Estado general",
  estado_tecnico: "Estado técnico (web/API/DB/recursos/SSL)",
  backups: "Copias de seguridad",
  emails: "Emails del sistema",
  citas: "Citas (24h, hoy, mañana)",
  vehiculos_alquiler: "Vehículos de alquiler",
  bonos: "Bonos y atenciones comerciales",
  clientes_leads: "Clientes y leads",
  errores_seguridad: "Errores y seguridad",
  modulos_clave: "Tabla de módulos clave",
};

const LEVEL_META = {
  ok: { label: "OK", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  aviso: { label: "Requiere atención", color: "bg-amber-100 text-amber-800 border-amber-200" },
  critico: { label: "Crítico", color: "bg-red-100 text-red-800 border-red-200" },
  partial: { label: "Parcial", color: "bg-amber-100 text-amber-800 border-amber-200" },
  error: { label: "Error", color: "bg-red-100 text-red-800 border-red-200" },
  skipped: { label: "Omitido", color: "bg-slate-100 text-slate-700 border-slate-200" },
  sending: { label: "Enviando…", color: "bg-blue-100 text-blue-800 border-blue-200" },
};

function Pill({ level }) {
  const m = LEVEL_META[level] || LEVEL_META.ok;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-semibold ${m.color}`}>
      {m.label}
    </span>
  );
}

export default function DailyReportPage() {
  const { axiosAuth, user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const [config, setConfig] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMeta, setPreviewMeta] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        axiosAuth().get("/admin/daily-report/config"),
        axiosAuth().get("/admin/daily-report/history?limit=25"),
      ]);
      setConfig(c.data);
      setRecipientsText((c.data.recipients || []).join(", "));
      setHistory(h.data.items || []);
    } catch (err) {
      toast.error("Error cargando configuración del informe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="daily-report-forbidden">
        <Card className="rounded-sm p-6 border border-amber-200 bg-amber-50">
          <AlertTriangle className="w-6 h-6 text-amber-700 inline mr-2" />
          <span className="text-sm text-amber-900">Esta página requiere rol super_admin o admin.</span>
        </Card>
      </div>
    );
  }

  const updateConfig = async (patch) => {
    setSaving(true);
    try {
      const res = await axiosAuth().put("/admin/daily-report/config", patch);
      setConfig(res.data);
      toast.success("Configuración actualizada");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error guardando configuración";
      toast.error(typeof detail === "string" ? detail : "Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  const saveRecipients = async () => {
    const list = recipientsText
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x.includes("@"));
    if (!list.length) {
      toast.error("Indica al menos un email válido (separados por coma).");
      return;
    }
    await updateConfig({ recipients: list });
  };

  const toggleSection = (key) => {
    const set = new Set(config?.sections || []);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    updateConfig({ sections: Array.from(set) });
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axiosAuth().post("/admin/daily-report/send-now");
      const r = res.data?.report || {};
      if (res.data?.skipped) {
        toast.success("Informe omitido (sin incidencias y 'Solo si hay incidencias' está activado)");
      } else if (res.data?.status === "ok") {
        toast.success(`Informe enviado a ${r.delivered?.length || 0} destinatario(s)`);
      } else if (res.data?.status === "partial") {
        toast.warning(`Enviado parcialmente. Errores en ${(r.errors || []).length} destinatario(s).`);
      } else {
        toast.error("No se pudo enviar el informe. Revisa el historial para más detalle.");
      }
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(typeof detail === "string" ? detail : "Error al enviar informe");
    } finally {
      setSending(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await axiosAuth().get("/admin/daily-report/preview");
      setPreviewHtml(res.data.html || "");
      setPreviewMeta({
        overall_level: res.data.overall_level,
        subject: res.data.subject,
        issues: res.data.issues || [],
      });
    } catch (err) {
      toast.error("Error generando vista previa");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading || !config) {
    return <div className="p-8 text-xs text-slate-500" data-testid="daily-report-loading">Cargando…</div>;
  }

  return (
    <div className="space-y-4 max-w-6xl" data-testid="daily-report-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-industrial text-2xl uppercase tracking-wide">
            <Mail className="w-5 h-5 inline mr-2 mb-1" /> Informes automáticos
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Email diario interno con estado del sistema, citas, alquiler, bonos y alertas operativas.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={loadPreview}
            disabled={previewLoading}
            className="rounded-sm text-xs uppercase"
            data-testid="dr-preview-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {previewLoading ? "Generando…" : "Vista previa"}
          </Button>
          <Button
            onClick={sendNow}
            disabled={sending || !config.recipients?.length}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs uppercase font-semibold"
            data-testid="dr-send-now-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Enviando…" : "Enviar informe ahora"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList className="rounded-sm">
          <TabsTrigger value="config" data-testid="dr-tab-config">Configuración</TabsTrigger>
          <TabsTrigger value="history" data-testid="dr-tab-history">Historial</TabsTrigger>
          <TabsTrigger value="preview" data-testid="dr-tab-preview">Vista previa</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4 mt-3">
          {/* Estado del envío */}
          <Card className="rounded-sm p-4 border border-slate-200">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Estado actual</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-400" />
                <span className="font-semibold">Activado:</span>
                <span data-testid="dr-status-enabled">{config.enabled ? "Sí" : "No"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="font-semibold">Último envío:</span>
                <span data-testid="dr-status-last">
                  {config.last_sent_at
                    ? new Date(config.last_sent_at).toLocaleString("es-ES")
                    : "—"}
                </span>
                {config.last_status && <Pill level={config.last_status} />}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="font-semibold">Hora prevista:</span>
                <span>{config.send_time} ({config.timezone})</span>
              </div>
            </div>
          </Card>

          {/* Toggle activar/desactivar + solo incidencias */}
          <Card className="rounded-sm p-4 border border-slate-200">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Comportamiento</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-semibold">Informe diario activado</p>
                  <p className="text-xs text-slate-500">Cuando se programe la automatización, se enviará a la hora indicada.</p>
                </div>
                <Switch
                  checked={!!config.enabled}
                  onCheckedChange={(v) => updateConfig({ enabled: v })}
                  disabled={saving}
                  data-testid="dr-toggle-enabled"
                />
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap border-t pt-3">
                <div>
                  <p className="text-sm font-semibold">Solo enviar si hay incidencias</p>
                  <p className="text-xs text-slate-500">Si está activado, solo se enviará cuando el estado sea "Requiere atención" o "Crítico".</p>
                </div>
                <Switch
                  checked={!!config.only_if_issues}
                  onCheckedChange={(v) => updateConfig({ only_if_issues: v })}
                  disabled={saving}
                  data-testid="dr-toggle-only-issues"
                />
              </div>
            </div>
          </Card>

          {/* Destinatarios */}
          <Card className="rounded-sm p-4 border border-slate-200">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Destinatarios</h3>
            <p className="text-xs text-slate-500 mb-2">Una o varias direcciones separadas por coma.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                placeholder="email1@empresa.com, email2@empresa.com"
                className="rounded-sm flex-1 min-w-[260px] text-xs"
                data-testid="dr-recipients-input"
              />
              <Button
                onClick={saveRecipients}
                disabled={saving}
                className="rounded-sm text-xs uppercase"
                data-testid="dr-recipients-save"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Guardar
              </Button>
            </div>
            {config.recipients?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {config.recipients.map((r) => (
                  <span key={r} className="text-[10px] bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-sm font-mono break-all">
                    {r}
                  </span>
                ))}
              </div>
            )}
          </Card>

          {/* Hora prevista */}
          <Card className="rounded-sm p-4 border border-slate-200">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Hora prevista (para automatización futura)</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="time"
                value={config.send_time}
                onChange={(e) => updateConfig({ send_time: e.target.value })}
                disabled={saving}
                className="rounded-sm w-32 text-xs"
                data-testid="dr-send-time-input"
              />
              <span className="text-[11px] text-slate-500">Zona: {config.timezone}</span>
              <span className="text-[10px] text-amber-600 ml-auto">
                Esta fase solo permite envío manual. La programación automática se activará en próxima iteración.
              </span>
            </div>
          </Card>

          {/* Secciones */}
          <Card className="rounded-sm p-4 border border-slate-200">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Secciones incluidas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(config.available_sections || []).map((key) => (
                <label
                  key={key}
                  className="flex items-center gap-2 p-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 text-xs"
                  data-testid={`dr-section-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={(config.sections || []).includes(key)}
                    onChange={() => toggleSection(key)}
                    disabled={saving}
                  />
                  <span className="break-words">{SECTION_LABELS[key] || key}</span>
                </label>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-3">
          <Card className="rounded-sm border border-slate-200 overflow-hidden">
            {history.length === 0 ? (
              <p className="p-6 text-xs text-slate-400 text-center" data-testid="dr-history-empty">
                Aún no hay envíos registrados.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Estado</th>
                      <th className="text-left p-2">Nivel</th>
                      <th className="text-left p-2">Destinatarios</th>
                      <th className="text-left p-2">Origen</th>
                      <th className="text-left p-2">Incidencias</th>
                    </tr>
                  </thead>
                  <tbody data-testid="dr-history-table">
                    {history.map((it) => (
                      <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-2 whitespace-nowrap">
                          {new Date(it.created_at).toLocaleString("es-ES")}
                        </td>
                        <td className="p-2"><Pill level={it.status} /></td>
                        <td className="p-2"><Pill level={it.overall_level} /></td>
                        <td className="p-2 max-w-[220px] break-all">
                          {(it.recipients || []).join(", ")}
                        </td>
                        <td className="p-2">
                          <span className="text-[10px] uppercase">{it.triggered_by || "—"}</span>
                          {it.triggered_by_user && (
                            <span className="block text-[10px] text-slate-400 break-all">{it.triggered_by_user}</span>
                          )}
                        </td>
                        <td className="p-2">{it.issues_count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="mt-3">
          <Card className="rounded-sm border border-slate-200 p-3">
            {!previewHtml ? (
              <div className="text-center py-6">
                <Button onClick={loadPreview} disabled={previewLoading} className="rounded-sm text-xs uppercase">
                  <Play className="w-3.5 h-3.5 mr-1.5" /> Generar vista previa ahora
                </Button>
                <p className="text-[11px] text-slate-400 mt-2">
                  Se generará el informe del momento actual sin enviarlo por email.
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <Pill level={previewMeta?.overall_level} />
                  <span className="text-xs font-mono break-all">{previewMeta?.subject}</span>
                  <Button variant="ghost" size="sm" onClick={loadPreview} className="ml-auto text-[10px] uppercase tracking-wider h-7 rounded-sm">
                    <RefreshCw className="w-3 h-3 mr-1" /> Regenerar
                  </Button>
                </div>
                <div
                  className="border border-slate-200 rounded-sm overflow-auto"
                  style={{ maxHeight: "70vh" }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                  data-testid="dr-preview-html"
                />
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
